import { useContext, useEffect, useState } from "react";
import { useGlobalAlert } from "../contexts/alertContext";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  startAfter,
  updateDoc,
} from "firebase/firestore";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, TextField } from "@mui/material";
import { db } from "../firebase-config";
import { convertCurrencyFormat } from "../helpers";
import ModalConfirmation from "../components/ModalConfirmation";
import CreateProductModal from "../components/CreateProductModal";
import { UserContext } from "../contexts/userContext";
import { ICategory, IProduct } from "../interfaces";
import {
  BUSINESS_COLLECTION,
  CATEGORIES_COLLECTION,
  STOCK_COLLECTION,
} from "../utils/constants";

const Stock = () => {
  const { businessData } = useContext(UserContext);
  const { showAlert } = useGlobalAlert();
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState({} as IProduct);
  const [list, setList] = useState<any>([] as IProduct[]);
  const [categories, setCategories] = useState([] as ICategory[]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  // (client-side search)
  // Guarda los snapshots de cada página para paginación bidireccional
  const [pageSnapshots, setPageSnapshots] = useState<any[]>([null]);
  const [totalItems, setTotalItems] = useState(0);
  const [openModal, setOpenModal] = useState(
    {} as { show: boolean; type: "delete" | "disable" | "" }
  );

  const pageSize = 10;

  const handleClose = () => {
    setShowNewModal(false);
    setOpenModal({ show: false, type: "" });
    setSelectedProduct({} as IProduct);
  };

  const getCategories = async () => {
    setLoading(true);
    const categoriesList = localStorage.getItem("categories") || "[]";

    if (categoriesList === "[]") {
      const categoriesCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        CATEGORIES_COLLECTION
      );
      const { docs } = await getDocs(categoriesCollectionRef).finally(() =>
        setLoading(false)
      );
      const list = docs.map(
        (doc) => ({ ...doc.data(), id: doc.id } as ICategory)
      );
      setCategories(list);
      localStorage.setItem("categories", JSON.stringify(list));
    } else setCategories(JSON.parse(categoriesList) as ICategory[]);
  };

  const getList = async () => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      STOCK_COLLECTION
    );
    setLoading(true);
    let q;
    if (page === 0) {
      q = query(
        subCollectionRef,
        orderBy("createdAt", "desc"),
        limit(pageSize)
      );
    } else {
      // Usar el snapshot guardado para la página actual
      const snapshot = pageSnapshots[page];
      if (snapshot) {
        q = query(
          subCollectionRef,
          orderBy("createdAt", "desc"),
          startAfter(snapshot),
          limit(pageSize)
        );
      } else {
        // fallback: ir a la primera página
        q = query(
          subCollectionRef,
          orderBy("createdAt", "desc"),
          limit(pageSize)
        );
      }
    }
    const { docs } = await getDocs(q).finally(() => setLoading(false));
    if (docs.length > 0) {
      setList(docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      // Solo guardar el snapshot si estamos avanzando
      if (pageSnapshots.length === page + 1) {
        setPageSnapshots((prev) => [...prev, docs[docs.length - 1]]);
      }
    } else setList([]);
  };

  // Refrescar la lista respetando si hay una búsqueda activa
  const refreshList = async () => {
    if ((searchTerm || "").trim() === "") {
      await getList();
    } else {
      // re-ejecuta la búsqueda con el término actual
      await handleSearch();
    }
  };

  // Buscar por nombre de producto (case-insensitive). Si searchTerm está vacío, volver al listado paginado.
  const handleSearch = async (evt?: any) => {
    // si se llamó por evento de teclado, asegurarse que sea Enter
    if (evt && !(evt.key === "Enter")) return;
    const term = (searchTerm || "").trim();
    if (term === "") {
      // resetear a vista paginada
      setPage(0);
      setPageSnapshots([null]);
      getList();
      return;
    }

    setLoading(true);
    try {
      const subCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        STOCK_COLLECTION
      );
      // Obtener todos los productos y filtrar en cliente por nombre
      const { docs } = await getDocs(
        query(subCollectionRef, orderBy("createdAt", "desc"))
      );
      const all = docs.map((d) => ({ ...d.data(), id: d.id } as IProduct));
      const filtered = all.filter((p) =>
        p.name?.toLowerCase().includes(term.toLowerCase())
      );
      if (filtered.length === 0) {
        setList([]);
        showAlert("No hay productos que coincidan con la búsqueda.", "warning");
      } else {
        setList(filtered);
      }
    } catch (error) {
      console.error("Error en búsqueda:", error);
      showAlert("Error al realizar la búsqueda.", "error");
    } finally {
      setLoading(false);
    }
  };

  // (no-op) búsqueda paginada removida; usamos búsqueda cliente en handleSearch

  const addOrRemoveCategory = async (categoryId: string, type: string) => {
    // Actualizar la cantidad de productos en la categoría correspondiente
    const categoriesCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      CATEGORIES_COLLECTION
    );
    const categoryDocRef = doc(categoriesCollectionRef, categoryId);
    try {
      await runTransaction(db, async (transaction) => {
        const categorySnap = await transaction.get(categoryDocRef);
        if (!categorySnap.exists()) throw new Error("Categoría no encontrada");
        const currentCount = categorySnap.data().products;
        transaction.update(categoryDocRef, {
          updatedAt: new Date(),
          products:
            type === "add"
              ? currentCount + 1
              : currentCount > 0
              ? currentCount - 1
              : 0,
        });
      });
      handleClose();
      await refreshList();
      showAlert(
        type === "add"
          ? "Producto agregado a la categoría."
          : "Producto eliminado de la categoría.",
        "success"
      );
    } catch (error) {
      console.error("Error al actualizar la cantidad de productos:", error);
      showAlert("Error al actualizar la cantidad de productos.", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (categoryId: string) => {
    const categoriesCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      CATEGORIES_COLLECTION
    );
    const categoryOrigin = selectedProduct.category_id;
    const categoryDocDestiny = doc(categoriesCollectionRef, categoryId);
    if (categoryOrigin) {
      // const categoryDestiny = getCategoryById(categoryId);
      const categoryDocOrigin = doc(categoriesCollectionRef, categoryOrigin);

      try {
        await runTransaction(db, async (transaction) => {
          const categoriaOrigenSnap = await transaction.get(categoryDocOrigin);
          const categoriaDestinoSnap = await transaction.get(
            categoryDocDestiny
          );
          if (!categoriaOrigenSnap.exists() || !categoriaDestinoSnap.exists()) {
            console.error("Uno de los documentos de categoría no existe.");
            return;
          }

          const datosOrigen = categoriaOrigenSnap.data();
          const datosDestino = categoriaDestinoSnap.data();

          // 2. Modificar las cantidades de productos
          const cantidadOrigenActual = datosOrigen.products || 0;
          const cantidadDestinoActual = datosDestino.products || 0;

          const nuevaCantidadOrigen =
            cantidadOrigenActual > 0 ? cantidadOrigenActual - 1 : 0;
          const nuevaCantidadDestino = cantidadDestinoActual + 1;

          // Actualizar los campos 'cantidadProductos' en la transacción
          transaction.update(categoryDocOrigin, {
            products: nuevaCantidadOrigen,
          });
          transaction.update(categoryDocDestiny, {
            products: nuevaCantidadDestino,
          });
        })
          .then(() => {
            handleClose();
            refreshList();
          })
          .finally(() => setLoading(false));
      } catch (error) {
        showAlert("Error al actualizar las categorías.", "error");
      }
    } else {
      try {
        await updateDoc(categoryDocDestiny, {
          products: 1,
        });
        handleClose();
        await refreshList();
        showAlert("Producto agregado a la nueva categoría.", "success");
      } catch (error) {
        showAlert("Error al agregar producto a la categoría.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const updateItem = async (item: any) => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      STOCK_COLLECTION
    );
    const productDoc = doc(subCollectionRef, item.id);
    try {
      await updateDoc(productDoc, item as any);
      if (item.category_id !== selectedProduct.category_id)
        await updateCategory(item.category_id);
      await refreshList();
      handleClose();
      setSelectedProduct({} as IProduct);
      showAlert("Producto actualizado correctamente.", "success");
    } catch (error) {
      showAlert("Error al actualizar el producto.", "error");
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const productDoc = doc(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      STOCK_COLLECTION,
      selectedProduct.id
    );
    try {
      await deleteDoc(productDoc);
      await addOrRemoveCategory(selectedProduct.category_id, "remove");
      showAlert("Producto eliminado correctamente.", "success");
    } catch (error) {
      showAlert("Error al eliminar el producto.", "error");
    } finally {
      handleClose();
    }
  };

  const handleSwitchCategory = () => {
    const itemToUpdate = {
      ...selectedProduct,
      status: selectedProduct.status === "enabled" ? "disabled" : "enabled",
    };
    // updateItem ya se encarga de refrescar la lista y cerrar el modal
    updateItem(itemToUpdate);
  };

  const handleDeleteOrSwitch = () => {
    switch (openModal.type) {
      case "delete":
        return handleDelete();
      case "disable":
        return handleSwitchCategory();
      default:
        return null;
    }
  };

  const openProductModal = () => setShowNewModal(true);

  const handleChangePage = (_: any, newPage: any) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (businessData.uid) getCategories();
  }, [businessData]);

  useEffect(() => {
    // Si hay searchTerm, obtener la página de búsqueda; si no, la lista normal
    if (!businessData.uid) return;
    if ((searchTerm || "").trim() === "") {
      getList();
    }
    // eslint-disable-next-line
  }, [page, businessData, searchTerm]);

  // Resetear snapshots si cambia el negocio
  useEffect(() => {
    setPage(0);
    setPageSnapshots([null]);
  }, [businessData.uid]);

  // useEffect para obtener el conteo total de documentos para la paginación
  useEffect(() => {
    if (businessData.uid) {
      const stockCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        STOCK_COLLECTION
      );
      setLoading(true);
      const fetchTotalCount = async () => {
        // Nota: Esta es una consulta costosa, hazla solo una vez si es posible
        const totalDocs = await getDocs(query(stockCollectionRef));
        setTotalItems(totalDocs.size);
      };
      fetchTotalCount();
    }
  }, [businessData]);
  return (
    <Grid
      container
      sx={{ width: "100%" }}
      display="flex"
      spacing={3}
      p={3}
      mt={10}
    >
      <Grid size={12} display="flex" alignItems="center">
        <Grid size={{ xs: 8, md: 10 }}>
          <Typography fontSize={24}>Inventario</Typography>
        </Grid>
        <Grid size={{ xs: 4, md: 2 }} justifyContent="end">
          <Button
            variant="contained"
            fullWidth
            size="small"
            color="secondary"
            onClick={openProductModal}
          >
            Nuevo producto
          </Button>
        </Grid>
      </Grid>
      <Grid size={12} display="flex" alignItems="center" mt={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar producto por nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleSearch()}
                    size="small"
                    aria-label="buscar"
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Grid size={12} display="flex" justifyContent="center">
        {loading ? (
          <CircularProgress size={120} sx={{ mt: 4 }} />
        ) : (
          <>
            {list.length > 0 ? (
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Precio publico</TableCell>
                      <TableCell>Cantidad</TableCell>
                      <TableCell>Categoria</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((product: IProduct) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>
                          {convertCurrencyFormat(Number(product.price))}
                        </TableCell>
                        <TableCell>{product.quantity}</TableCell>
                        <TableCell>{product.category_name || "-"}</TableCell>
                        <TableCell>
                          {product.type === "counter" ? "Barra" : "Cocina"}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            color={
                              product.status === "enabled"
                                ? "success"
                                : "warning"
                            }
                            label={
                              product.status === "enabled"
                                ? "Habilitada"
                                : "Deshabilitada"
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => {
                                setSelectedProduct(product);
                                openProductModal();
                              }}
                            >
                              <span className="material-symbols-rounded">
                                edit
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              color="error"
                              onClick={() => {
                                setSelectedProduct(product);
                                setOpenModal({ show: true, type: "delete" });
                              }}
                            >
                              <span className="material-symbols-rounded">
                                delete
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              product.status === "enabled"
                                ? "Deshabilitar"
                                : "Habilitar"
                            }
                          >
                            <Switch
                              size="small"
                              color="secondary"
                              checked={product.status === "enabled"}
                              onChange={() => {
                                setSelectedProduct(product);
                                setOpenModal({ show: true, type: "disable" });
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {/* No mostrar paginación cuando hay búsqueda activa; list contiene todos los matches */}
                {(searchTerm || "").trim() === "" && (
                  <TablePagination
                    rowsPerPageOptions={[pageSize]}
                    rowsPerPage={pageSize}
                    component="div"
                    page={page}
                    count={totalItems}
                    onPageChange={(_e, newPage) => setPage(newPage)}
                  />
                )}
              </TableContainer>
            ) : (
              <Alert severity="warning">
                {(searchTerm || "").trim() !== ""
                  ? "No hay productos que coincidan con la búsqueda."
                  : "No hay productos cargados."}
              </Alert>
            )}
          </>
        )}
      </Grid>

      {openModal.show && (
        <ModalConfirmation
          page="product"
          status={selectedProduct.status}
          openModal={openModal}
          handleClose={handleClose}
          handleSubmit={handleDeleteOrSwitch}
        />
      )}
      {showNewModal && (
        <CreateProductModal
          show={showNewModal}
          categories={categories}
          product={selectedProduct}
          handleClose={handleClose}
          updateItem={updateItem}
          addCategory={addOrRemoveCategory}
        />
      )}
    </Grid>
  );
};

export default Stock;
